import { Button } from '~/components/common/Button'

import { Popover, PopoverContent, PopoverTrigger, useDisclosure } from '@nextui-org/react'
import React, { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { SimpleTextarea } from '~/components/common/SimpleTextarea'
import { useConversationUuid } from '~/hooks'
import { useTaskId } from '~/hooks/task/useTaskId'
import { DocumentInlineComment } from '~/protos/agents'
import { regenerateDoc } from '~/services/taskService'
import { OpenDocCommentPopoverEvent } from '~/utils/eventUtils'
import { AppEvents, globalEventEmitter } from '~/utils/shared'
import { BetterToast } from '~/utils/toast'

// Wrapper component that properly forwards refs for PopoverTrigger
const CursorLocator = React.forwardRef<
  HTMLDivElement,
  { localRef: React.RefObject<HTMLDivElement | null> }
>(function CursorLocator({ localRef }, forwardedRef) {
  return (
    <div
      className='absolute cursor-locator w-[1px] h-[1px] bg-transparent top-0'
      ref={el => {
        // Merge the forwarded ref from PopoverTrigger with our local ref
        if (typeof forwardedRef === 'function') {
          forwardedRef(el)
        } else if (forwardedRef) {
          forwardedRef.current = el
        }
        if (localRef) {
          ;(localRef as React.MutableRefObject<HTMLDivElement | null>).current = el
        }
      }}
    />
  )
})

export function DocReviewPopover() {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const {
    isOpen: isReviewModalOpen,
    onOpenChange: onReviewModalClose,
    onOpen: onReviewModalOpenChange
  } = useDisclosure()
  const cursorLocatorRef = useRef<HTMLDivElement | null>(null)
  const [commentText, setCommentText] = useState('')
  const [startLineNumber, setStartLineNumber] = useState(0)
  const [endLineNumber, setEndLineNumber] = useState(0)
  const uuid = useConversationUuid()
  const taskId = useTaskId()
  const { workspaceUuid } = useParams<{ workspaceUuid: string }>()

  useEffect(() => {
    const onOpenDocCommentPopover = (event: OpenDocCommentPopoverEvent) => {
      const cursorLocator = cursorLocatorRef.current
      const container = containerRef.current
      if (cursorLocator != null && container != null) {
        const relativeTop = event.clientY - container.getBoundingClientRect().top
        const lineHeight = 20 * (event.endLine - event.startLine)
        const POPOVER_MARGIN_TOP = 10
        cursorLocator.style.top = `${relativeTop + lineHeight + POPOVER_MARGIN_TOP}px`
        cursorLocator.style.left = `20px`
        setStartLineNumber(event.startLine)
        setEndLineNumber(event.endLine)
      }
      onReviewModalOpenChange()
    }
    globalEventEmitter.globalOn(AppEvents.OpenDocCommentPopover, (event: unknown) =>
      onOpenDocCommentPopover(event as OpenDocCommentPopoverEvent)
    )
  }, [uuid, onReviewModalOpenChange])

  const [isDisabled, setIsDisabled] = useState(false)
  const regenerateDocument = async () => {
    if (!taskId) {
      BetterToast.error('Task ID not available')
      return
    }

    if (!workspaceUuid) {
      BetterToast.error('Workspace not found')
      return
    }

    setIsDisabled(true)
    await regenerateDoc({
      workspaceUuid,
      uuid,
      taskId,
      comment: DocumentInlineComment.fromJSON({
        comment: commentText,
        startLineNumber,
        endLineNumber
      })
    })
    BetterToast.success('Document regeneration started')
    setIsDisabled(false)
    onReviewModalClose()
  }

  const onCancel = () => {
    globalEventEmitter.emit(AppEvents.OpenDocCommentPopoverCallback, { action: 'cancel' })
    onReviewModalClose()
  }

  return (
    <div
      className='doc-reivew-popover w-full h-full absolute top-0 left-0 right-0 bottom-0 z-[-1]'
      ref={containerRef}
    >
      <Popover
        isOpen={isReviewModalOpen}
        onOpenChange={onReviewModalOpenChange}
        placement='bottom-start'
        className='w-[450px]'
        classNames={{
          content: 'rounded-md shadow-2xl p-4 border-none ring-1 ring-[#E5E7EB]'
        }}
      >
        <PopoverTrigger>
          <CursorLocator localRef={cursorLocatorRef} />
        </PopoverTrigger>
        <PopoverContent>
          <div className='flex flex-col w-full'>
            <SimpleTextarea
              minRows={5}
              placeholder='Regenerate with comment...'
              autoFocus
              value={commentText}
              onChange={setCommentText}
            />
            <div className='buttons flex justify-end mt-4'>
              <Button variant='outlined' onClick={onCancel} className='mr-1'>
                Cancel
              </Button>
              <Button
                variant='primary'
                onClick={regenerateDocument}
                className='ml-1'
                isDisabled={isDisabled}
              >
                Regenerate
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
